// library imports
import { Empty, message, Segmented, Flex, Card, Row, Col } from "antd";
import { useEffect, useState, useContext } from "react";
import {
  ClockCircleOutlined,
  HeartOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
// component imports
import Analytics from "../../components/analytics/Analytics";
import CustomLineBarChart from "../../components/common/CustomLineBarChart";
import { VerticalDateFilter } from "../../components/common/DateFilter";
import StateG2Chart from "../../components/common/StateG2Chart";
// context imports
import { IndexContext } from "../../context/IndexContext";
// hook imports
import useFetch from "../../hooks/useFetch";
// config imports
import {
  DARK_BLUE,
  LIGHT_BLUE,
  LIGHT_LIGHT_BLUE,
  DARK_GREEN,
  LIGHT_GREEN,
  ORANGE,
  DARK_THEME_DARK_BLUE,
  PURPLE,
} from "../../configs/configs";

const dateTypeOptions = [
  { label: "Day", value: "date" },
  { label: "Month", value: "month" },
  { label: "Year", value: "year" },
];

export default function DataHealthPage() {
  const [fetchWrapper] = useFetch();
  const {
    siteSelected,
    notificationApi,
    isLightTheme,
    subscribeAiBess,
    hasGensetSelected,
    hasTnbSelected,
  } = useContext(IndexContext);

  const [loadingDataHealth, setLoadingDataHealth] = useState(false);
  const [isTrendingDataLoading, setIsTrendingDataLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [startDateSelection, setStartDateSelection] = useState(
    dayjs().subtract(6, "day"),
  );
  const [endDateSelection, setEndDateSelection] = useState(dayjs());

  const [dateTypeSelection, setDateType] = useState(dateTypeOptions[0].value);

  const [siteCommissioningDate, setSiteCommissioningDate] = useState(null);

  const [powerDemandData, setPowerDemandData] = useState([]);
  const [loadData, setLoadData] = useState([]);

  const [chartRange, setChartRange] = useState([]);
  const [chartDomain, setChartDomain] = useState([]);

  const [stateData, setStateData] = useState([]);

  const [stateTime, setStateTime] = useState([]);

  const onFilterClick = () => {
    if (startDateSelection > endDateSelection) {
      message.error("End date must be later than or same as start date.");
    } else if (endDateSelection.diff(startDateSelection, "day") > 31) {
      message.warning("Date range cannot exceed 31 days.");
    } else {
      getPowerDemandData();
      getHealthData();
    }
  };

  const onExportClick = () => {
    if (powerDemandData.length < 1) {
      message.error("No data to export");
    } else {
      if (powerDemandData.length < 1 && loadData.length < 1) {
        message.error("No data to export");
      } else {
        downloadHealthData();
      }
    }
  };

  /** API to get the power demand data */
  function getPowerDemandData() {
    setIsTrendingDataLoading(true);
    fetchWrapper({
      endpoint_url: "power_demand/getPowerDemandData",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
        start_date: startDateSelection
          .startOf("day")
          .format("YYYY-MM-DD HH:mm:ss"),
        end_date: endDateSelection.endOf("day").format("YYYY-MM-DD HH:mm:ss"),
        date_selection:
          dateTypeSelection === "date" ? "day" : dateTypeSelection, // TODO: change backend. backend expects "day" instead of "date"
      },

      onSuccess: (response) => {
        setPowerDemandData(response.data.power_demand_trending_data);
        setLoadData(response.data.load_data);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get power demand data.",
          description: response.statusText,
        });
      },

      onFinish: () => {
        setIsTrendingDataLoading(false);
      },
    });
  }

  function getHealthData() {
    setLoadingDataHealth(true);
    fetchWrapper({
      endpoint_url: "analytics/getHealthData",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
        start_date: startDateSelection
          .startOf("day")
          .format("YYYY-MM-DD HH:mm:ss"),
        end_date: endDateSelection.endOf("day").format("YYYY-MM-DD HH:mm:ss"),
        date_selection: dateTypeSelection,
      },

      onSuccess: (response) => {
        setStateData(response.data.state_data);
        setStateTime(response.data.state_time);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get health data.",
          description: response.statusText,
        });
      },

      onFinish: () => {
        setLoadingDataHealth(false);
      },
    });
  }

  /** API to download power demand data as excel */
  function downloadHealthData() {
    setDownloadLoading(true);

    fetchWrapper({
      endpoint_url: "analytics/downloadHealthData",
      method: "POST",
      json_output: false,
      body: {
        site_id: siteSelected,
        start_date: startDateSelection
          .startOf("day")
          .format("YYYY-MM-DD HH:mm:ss"),
        end_date: endDateSelection.endOf("day").format("YYYY-MM-DD HH:mm:ss"),
        date_selection: dateTypeSelection,
        power_demand_data: powerDemandData,
        load_data: loadData,
      },

      onSuccess: (response) => {
        if (response.headers.get("content-type") !== "application/json") {
          // no issue with the response. returned a spreadsheet
          response.blob().then((blob) => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `Health_Data_${dayjs().format(
              "YYYYMMDD_HHmmss",
            )}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
          });
        }
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to download file",
        });
      },

      onFinish: () => {
        setDownloadLoading(false);
      },
    });
  }

  /* API to get site commissioning date */
  function getSiteCommissioningDate() {
    fetchWrapper({
      endpoint_url: "common/getSiteCommissioningDate",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
      },

      onSuccess: (response) => {
        setSiteCommissioningDate(response.data.site_commissioning_date);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get site commissioning date.",
          description: response.statusText,
        });
      },
    });
  }

  useEffect(() => {
    setPowerDemandData([]);
    setLoadData([]);
  }, [siteSelected]);

  useEffect(() => {
    if (subscribeAiBess === true) {
      if (hasGensetSelected === true) {
        if (hasTnbSelected === false) {
          setChartDomain([
            "PV (Self-Consumption)",
            "BESS Discharge",
            "BESS Charge",
            "Load",
            "Genset",
          ]);
          setChartRange(
            isLightTheme
              ? [DARK_GREEN, LIGHT_BLUE, LIGHT_LIGHT_BLUE, ORANGE, PURPLE]
              : [DARK_GREEN, LIGHT_BLUE, LIGHT_LIGHT_BLUE, ORANGE, PURPLE],
          );
        } else {
          setChartDomain([
            "PV (Self-Consumption)",
            "PV (Export)",
            "BESS Discharge",
            "BESS Charge",
            "TNB",
            "Load",
            "Genset",
          ]);
          setChartRange(
            isLightTheme
              ? [
                  DARK_GREEN,
                  LIGHT_GREEN,
                  LIGHT_BLUE,
                  LIGHT_LIGHT_BLUE,
                  DARK_BLUE,
                  ORANGE,
                  PURPLE,
                ]
              : [
                  DARK_GREEN,
                  LIGHT_GREEN,
                  LIGHT_BLUE,
                  LIGHT_LIGHT_BLUE,
                  DARK_THEME_DARK_BLUE,
                  ORANGE,
                  PURPLE,
                ],
          );
        }
      } else {
        setChartDomain([
          "PV (Self-Consumption)",
          "PV (Export)",
          "BESS Discharge",
          "BESS Charge",
          "TNB",
          "Load",
        ]);
        setChartRange(
          isLightTheme
            ? [
                DARK_GREEN,
                LIGHT_GREEN,
                LIGHT_BLUE,
                LIGHT_LIGHT_BLUE,
                DARK_BLUE,
                ORANGE,
              ]
            : [
                DARK_GREEN,
                LIGHT_GREEN,
                LIGHT_BLUE,
                LIGHT_LIGHT_BLUE,
                DARK_THEME_DARK_BLUE,
                ORANGE,
              ],
        );
      }
    } else {
      if (hasGensetSelected === true) {
        setChartDomain([
          "PV (Self-Consumption)",
          "PV (Export)",
          "TNB",
          "Load",
          "Genset",
        ]);
        setChartRange(
          isLightTheme
            ? [DARK_GREEN, LIGHT_GREEN, DARK_BLUE, ORANGE, PURPLE]
            : [DARK_GREEN, LIGHT_GREEN, DARK_THEME_DARK_BLUE, ORANGE, PURPLE],
        );
      }
    }
  }, [subscribeAiBess, hasGensetSelected, hasTnbSelected]);

  useEffect(() => {
    if (siteSelected) {
      getSiteCommissioningDate();
    }
  }, [siteSelected]);

  // when site commissioning date changes, need to update start date
  useEffect(() => {
    if (siteCommissioningDate) {
      setStartDateSelection(
        dayjs(
          Math.max(
            dayjs().subtract(6, "day").valueOf(),
            dayjs(siteCommissioningDate, "YYYY-MM-DD").valueOf(),
          ),
        ),
      );
    }
  }, [siteCommissioningDate]);

  return (
    <Analytics
      title={"Power Demand Distribution & Data Health"}
      contentComponent={
        powerDemandData.length > 0 ? (
          <div>
            <div style={{ width: "100%", height: "350px" }}>
              <CustomLineBarChart
                dataLine={loadData.map((item) => {
                  const newItem = { ...item };
                  for (const key in newItem) {
                    if (newItem[key] === "-") {
                      newItem[key] = null;
                    }
                  }
                  return newItem;
                })}
                dataBar={powerDemandData.map((item) => {
                  const newItem = { ...item };
                  for (const key in newItem) {
                    if (newItem[key] === "-") {
                      newItem[key] = null;
                    }
                  }
                  return newItem;
                })}
                xAxisLine="date"
                yAxisLine="Load"
                xAxisBar="date"
                yAxisBar="value"
                xAxisTitleBar="Time"
                yAxisTitleBar="Power Demand (kW)"
                lineColor="type"
                barColor="type"
                barChartRange={chartRange}
                barChartDomain={chartDomain}
                lineChartColor={ORANGE}
              />
            </div>

            <Card
              title={
                <Flex justify="flex-start" gap={"small"}>
                  <HeartOutlined /> DATA HEALTH
                </Flex>
              }
              size="small"
              loading={loadingDataHealth}
            >
              <Row>
                <Col span={24}>
                  <div
                    style={{
                      width: "100%",
                      height: `${Math.max(80, new Set((stateData ?? []).map((d) => d.device)).size * 40 + 40)}px`,
                    }}
                  >
                    {stateTime.length > 0 && !stateTime.includes("--:--:--") ? (
                      <StateG2Chart
                        data={stateData ?? []}
                        containerName={"state-g2-chart"}
                        shiftTime={stateTime}
                      />
                    ) : stateData ? (
                      <StateG2Chart
                        data={stateData ?? []}
                        containerName={"state-g2-chart"}
                        shiftTime={[
                          dayjs().format("YYYY-MM-DDT00:00:00"),
                          dayjs().format("YYYY-MM-DDT23:59:59"),
                        ]}
                      />
                    ) : (
                      <Flex
                        justify="center"
                        children={<Empty style={{ scale: "0.45" }} />}
                      />
                    )}
                  </div>
                </Col>
              </Row>
            </Card>
          </div>
        ) : (
          <div>
            <Empty />
          </div>
        )
      }
      isTrendingDataLoading={isTrendingDataLoading}
      filterComponent={
        <div className="analytics-picker-type-segmented">
          {/* <Flex vertical={true} gap={"8px"}>
            <span>Graph Type Selection</span>
            <Segmented
              style={{ marginBottom: "12px" }}
              options={dateTypeOptions}
              onChange={(value) => {
                setDateType(value);
              }}
              value={dateTypeSelection}
            />
          </Flex> */}
          <VerticalDateFilter
            startDateSelection={startDateSelection}
            setStartDateSelection={setStartDateSelection}
            endDateSelection={endDateSelection}
            setEndDateSelection={setEndDateSelection}
            searchLoading={isTrendingDataLoading}
            downloadLoading={downloadLoading}
            onFilterClick={onFilterClick}
            onExportClick={onExportClick}
            pickerType={dateTypeSelection}
            minDate={dayjs(siteCommissioningDate, "YYYY-MM-DD")}
            maxDate={dayjs()}
          />
        </div>
      }
    />
  );
}
